import * as Sentry from '@sentry/svelte';
import { device_log_entry, device_log_tail } from '$lib/tools/device_log';

/**
 * Centralized Sentry initialization + error reporting for the app.
 *
 * This module MUST be imported before any graph-db/transport wiring runs
 * (see `graphdb.ts` and `+layout.svelte`): module-scope `import` order is
 * guaranteed, so `import '$lib/tools/sentry'` first means `Sentry.init()`
 * runs before `init_ws` / `init_http` are called.
 *
 * `__SENTRY_RELEASE__` is injected at build time by the @sentry/vite-plugin
 * (see vite.config.ts). In dev it may be undefined — that's fine.
 */
declare const __SENTRY_RELEASE__: string | undefined;

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

const enabled = typeof window !== 'undefined' && typeof dsn === 'string' && dsn.length > 0;

// Initialize once at module scope (guard against HMR double-imports).
if (enabled && !Sentry.getClient()) {
	Sentry.init({
		dsn,
		tracesSampleRate: 1.0,
		environment: import.meta.env.MODE ?? 'production',
		release: typeof __SENTRY_RELEASE__ === 'string' ? __SENTRY_RELEASE__ : undefined,
		beforeSend: sentry_before_send
	});
	// Boot probe: one deliberately visible event per app launch so "zero
	// Sentry" becomes diagnosable — is Bugsink/DSN/init order broken on this
	// device, or is the app genuinely error-free? The /debug screen has a
	// "send test event" button for an on-demand check.
	Sentry.captureMessage('device_log.boot_probe', {
		level: 'warning',
		extra: {
			env: import.meta.env.MODE ?? 'production',
			release: typeof __SENTRY_RELEASE__ === 'string' ? __SENTRY_RELEASE__ : undefined,
			at: new Date().toISOString()
		}
	});
}
device_log_entry('info', 'sentry.init', { enabled, has_dsn: Boolean(dsn) });

export function is_sentry_enabled(): boolean {
	return enabled;
}

/**
 * beforeSend filter: drop known noise so the board stays readable, while
 * keeping every real error (WS failures, HTTP failures, auth troubles).
 *
 * Extracted (and exported) so the noise policy is unit-testable. The generic
 * `E` keeps the Sentry.init binding type-clean while allowing partial events
 * in tests.
 */
export function sentry_before_send<E extends { message?: string; exception?: { values?: Array<{ value?: string }> } }>(
	event: E
): E | null {
	// The raw-Event garbage has NO top-level message: Sentry's
	// captureException(Event) puts the description in exception.values[0].value
	// (trace dump shows exactly that — `message` is only set for string input).
	const msg = event.message ?? event.exception?.values?.[0]?.value ?? '';

	// Safety net: Sentry serializes a captured raw Event (e.g. a WebSocket
	// `error` Event — which ws.ts now keeps as breadcrumbs) into the useless
	// "Event `Event` (type=error) captured as exception". If any path ever
	// captures a raw Event again, drop it instead of polluting the board.
	if (/^Event `[^`]+` \(type=\w+\) captured as exception$/.test(msg)) return null;

	// Browser-native noise when a socket dies before the handshake completes.
	if (/WebSocket is closed before the connection is established/i.test(msg)) return null;

	return event;
}

/**
 * Record a breadcrumb for the event trail, then capture an exception when
 * Sentry is enabled. Always `console.error`s too so local dev sees it.
 *
 * @param error The error/string to report.
 * @param context Extra context: message, tags, extras
 */
export function report_error(
	error: unknown,
	context: {
		message?: string;
		tags?: Record<string, string>;
		extras?: Record<string, unknown>;
		level?: Sentry.SeverityLevel;
	} = {}
): void {
	const { message, tags, extras, level = 'error' } = context;

	if (enabled) {
		Sentry.addBreadcrumb({
			category: 'app.error',
			message: message ?? String(error),
			level,
			data: extras
		});
		Sentry.captureException(error, {
			tags: tags ?? undefined,
			extra: { ...(extras ?? {}), device_log_tail: device_log_tail(12) }
		});
	}

	// Keep local dev debuggable even without Sentry.
	if (message) {
		console.error(`[${tags?.component ?? 'app'}] ${message}`, { error, ...extras });
	} else {
		console.error(error, extras);
	}
}

/**
 * Record a breadcrumb-only event (no exception). Used for mid-flight WS
 * lifecycle tracing (connecting/opened/connected) and HTTP requests so the
 * Sentry board shows the full trail leading up to a failure.
 */
export function trace(message: string, category: string, data?: Record<string, unknown>): void {
	if (enabled) {
		Sentry.addBreadcrumb({
			category,
			message,
			level: 'info',
			data
		});
	}
}

/**
 * Convenience wrapper matching the transport layer's needs: report a WS
 * lifecycle event with endpoint + phase tags attached.
 */
export function report_ws(phase: string, error: unknown | null, extras: Record<string, unknown> = {}): void {
	if (error) {
		report_error(error, {
			message: `ws.${phase}`,
			tags: { component: 'ws', phase },
			extras
		});
	} else {
		trace(`ws.${phase}`, 'ws', extras);
	}
}
